import React, {
  useState,
  useEffect,
  type ChangeEvent,
  useRef,
  useContext,
} from 'react';
import { TextField, TextFieldHelperText } from '@rmwc/textfield';
import classNames from 'classnames';
import { type RootState } from '../../store'; // Adjust according to your store setup
import { updateField } from '../../actions/actionCreators';
import errorMessage from '../../utils/errorMessage';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { FieldsetValidatorContext } from './Form';
import { validators } from '../../utils/validators';

interface TextFieldWrapperProps {
  name: string;
  label?: string;
  required?: boolean;
  form: string;
  textarea?: boolean;
  rows?: number | string;
  fullWidth?: boolean;
  heading?: string;
  className?: string;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  minLength?: number;
  hidden?: boolean;
  column?: boolean;
  preferences?: boolean;

}

const TextFieldWrapper: React.FC<TextFieldWrapperProps> = ({
  name,
  label,
  required,
  form,
  textarea,
  rows,
  fullWidth,
  heading,
  className,
  placeholder,
  type,
  maxLength,
  minLength,
  hidden,
  column,
  preferences,
}) => {
  const el = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const value = useAppSelector(
    (state: RootState) => state.forms[form]?.[name]?.value || ''
  );
  const labelFromState = useAppSelector(
    (state: RootState) => state.forms[form]?.[name]?.label || ''
  );
  const currentStep = useAppSelector(
    (state: RootState) => (state.forms as any)?.currentStep || 0
  );
  const [error, setError] = useState<string | null>(null);
  const [displayError, setDisplayError] = useState(false);
  const fieldsetValidator = useContext(FieldsetValidatorContext);

  // Use label from Redux state if available, otherwise use prop label
  const displayLabel = labelFromState || label;

  const validate = () => {
    // First check if required field is filled
    if (required && !validators.required(value)) {
      const errorMessages = errorMessage(
        {
          name,
          label: displayLabel,
          required,
          el: el.current,
          type: 'required',
        },
        false
      );
      setError(errorMessages || null);
      return false;
    }

    // If field has value and is email type, validate email format
    if (value && type === 'email' && !validators.email(value)) {
      const errorMessages = errorMessage(
        {
          name,
          label: displayLabel,
          required,
          el: el.current,
          type: 'email',
        },
        false
      );
      setError(errorMessages || null);
      return false;
    }

    // If field has minLength requirement, validate minimum length
    if (value && minLength && !validators.minLength(value, minLength)) {
      const errorMessages = validators.message(
        displayLabel || name,
        'minLength',
        minLength
      );
      setError(errorMessages || null);
      return false;
    }

    setError(null);
    return true;
  };

  const validateAndRegister = () => {
    const valid = validate();

    if (el.current) {
      fieldsetValidator.register({
        name,
        required,
        el: el.current,
        valid,
      });
    }

    return valid;
  };

  useEffect(() => {
    validateAndRegister();

    return () => fieldsetValidator.unregister(name);
  }, [el, value]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let newValue = e.target.value;

    // For phone number fields, only allow numbers, spaces, parentheses, and dashes
    if (type === 'tel' || name === 'phoneNumber' || name === 'repPhone') {
      newValue = newValue.replace(/[^0-9\s()-+]/g, '');
    }

    dispatch(updateField(form, name, newValue));

    setDisplayError(true);
  };

  const placeholderText = preferences
    ? // eslint-disable-next-line max-len
      'For example\nI need a translator\nI have a hearing impairment\nI prefer email communication\nI can’t answer the phone at certain times.'
    : placeholder;

  return (
    <>
      {heading && <h3 className="step__heading mt--0">{heading}</h3>}
      <div
        className={classNames(className, 'form__fieldholder', {
          'form__fieldholder--error': error,
          'form__fieldholder--hasValue': value,
          'form__fieldholder--hidden': hidden,
          'form__fieldholder--column': column,
          'form__fieldholder--fullWidth': fullWidth,
        })}
        ref={el}
      >
        <TextField
          key={`${name}-${currentStep}`}
          type={type}
          name={name}
          label={required && displayLabel ? `${displayLabel} *` : displayLabel}
          id={name}
          value={value}
          onChange={handleChange}
          onBlur={() => {
            setDisplayError(true);
            validateAndRegister();
          }}
          onKeyDown={(e) => {
            // Handle space key specifically since it's being blocked
            if (e.key === ' ' || e.keyCode === 32) {
              const input = e.target as HTMLInputElement;
              const cursorPosition = input.selectionStart || 0;
              const selectionEnd = input.selectionEnd || 0;
              const currentValue = value || '';

              let newValue;
              let newCursorPosition;

              if (cursorPosition !== selectionEnd) {
                newValue = `${currentValue.slice(0, cursorPosition)} ${currentValue.slice(selectionEnd)}`;
                newCursorPosition = cursorPosition + 1;
              } else {
                newValue = `${currentValue.slice(0, cursorPosition)} ${currentValue.slice(cursorPosition)}`;
                newCursorPosition = cursorPosition + 1;
              }

              setTimeout(() => {
                if (input) {
                  input.value = newValue;
                  input.setSelectionRange(newCursorPosition, newCursorPosition);
                  const changeEvent = new Event('input', { bubbles: true });
                  input.dispatchEvent(changeEvent);
                }
              }, 0);
            }
          }}
          required={required}
          textarea={textarea}
          placeholder={placeholderText}
          rows={typeof rows === 'string' ? parseInt(rows, 10) : rows}
          fullwidth={fullWidth}
          className={className}
          maxLength={maxLength}
        />
        {displayError && error && (
          <>
            <TextFieldHelperText persistent validationMsg>
              {error}
            </TextFieldHelperText>
            <div className="validation-icon validation-wrong"></div>
          </>
        )}
        {value.length > 0 && !error && (
          <div className="validation-icon validation-ok"></div>
        )}
      </div>
    </>
  );
};

export default TextFieldWrapper;
